/**
 * Flightline — Fleet acquisition engine
 * --------------------------------------------------------------------------
 * Location : /src/domain/fleet/acquisitionEngine.ts
 * Runs in  : Simulation Web Worker (pure functions, no UI or DOM concerns).
 *
 * Five ways to add or move airframes, all pure. Each command takes the
 * current `FleetAcquisitionState` and returns a new one, plus the cash
 * movements to post to the ledger. Nothing mutates the caller's data.
 *
 *   Factory order (OEM direct)  Deposit at order (10 %), lead time 6–24 months,
 *                               price escalates over the lead time. Cancelling
 *                               costs a penalty of 10–25 % of the locked price,
 *                               with the deposit credited against it.
 *   Secondary purchase          Used airframe from the monthly catalogue, with
 *                               higher hours, a sooner D-Check and a fuel penalty.
 *   Dry lease                   Lower rent; airline crews, fuels and maintains.
 *                               Two months' deposit (refunded at a clean return).
 *   Wet lease (ACMI)            Premium short-term fee; lessor supplies crew,
 *                               maintenance and insurance. Airline pays fuel.
 *   Finance lease               Amortising loan with a balloon; title transfers
 *                               when the balloon is paid at term end.
 *   Sale and leaseback          Sell an owned airframe to a lessor and lease it
 *                               back as a dry lease.
 *   Termination                 Early exit with a fee; finance leases pay off
 *                               the loan and take title.
 *
 * Time is `SimMinutes`. The daily cycle (`dailyLeaseCycle`) posts instalments
 * and expiries; the caller runs it each day with the current fleet.
 * Registrations come from `nextSequence`; the caller must start that above the
 * highest existing FL- registration.
 *
 * Not yet wired into SimulationEngine. Cabins are passed in and not validated
 * here; the caller runs cabinValidator first.
 */

import { clamp, pad } from '../ai/aiMath';
import type {
  AcquisitionTerms,
  AircraftInstance,
  AircraftType,
  AirportId,
  CabinConfiguration,
  FinanceLeaseTerms,
  MaintenanceStatus,
  OperatingLeaseTerms,
  PurchaseTerms,
  Registration,
  SimMinutes,
  UsdAmount,
} from '../models';
import {
  DRY_EARLY_TERMINATION_RENT_SHARE,
  DRY_LEASE_DEPOSIT_MONTHS,
  DRY_LEASE_TERM_MONTHS,
  FACTORY_LEAD_MONTHS_MAX,
  FACTORY_LEAD_MONTHS_MIN,
  FINANCE_BALLOON_RATE,
  FINANCE_DOWN_PAYMENT_RATE,
  FINANCE_LEASE_TERM_MONTHS,
  FINANCE_PREPAYMENT_FEE_RATE,
  LEASE_AGE_YEARS,
  MINUTES_PER_DAY,
  MINUTES_PER_MONTH,
  MONTHS_PER_YEAR,
  RETURN_MIN_D_CHECK_MONTHS,
  SALE_LEASEBACK_VALUE_FACTOR,
  SYNTHETIC_HOURS_PER_CYCLE,
  SYNTHETIC_HOURS_PER_YEAR,
  WET_EARLY_TERMINATION_MONTHS,
  WET_LEASE_TERM_MONTHS,
  airframeAgeYears,
  appraisedValueUSD,
  calendarYearOf,
  dryMonthlyRentUSD,
  factoryCancellationRate,
  factoryDepositUSD,
  factoryListPriceUSD,
  factoryLockedPriceUSD,
  financeAnnualRatePct,
  fuelBurnMultiplierForAge,
  monthlyInstalmentUSD,
  wetMonthlyFeeUSD,
  yearsToMinutes,
} from './fleetMath';
import { buildSecondaryCatalog, monthIndexOfDay, refreshSecondaryCatalog } from './secondaryMarket';
import type {
  AcquisitionRejection,
  AcquisitionRejectionCode,
  AirframeProfile,
  FactoryOrder,
  FleetAcquisitionState,
  FleetCashMovement,
  FleetContext,
  FleetResult,
  LeaseContract,
  LeaseContractBase,
  LeaseSubType,
  MarketEnvironment,
  SecondaryListing,
} from './types';

/* ========================================================================== *
 * 1. REQUEST, RESULT AND STATE SHAPES
 * ========================================================================== */

export interface FactoryOrderRequest {
  readonly typeId: string;
  readonly deliveryLeadMonths: number;
  readonly cabin: CabinConfiguration;
}

export interface FactoryQuote {
  readonly typeId: string;
  readonly deliveryLeadMonths: number;
  readonly deliveryAt: SimMinutes;
  readonly listPriceUSD: UsdAmount;
  readonly lockedPriceUSD: UsdAmount;
  readonly depositUSD: UsdAmount;
  readonly balanceAtDeliveryUSD: UsdAmount;
}

export interface PurchaseSecondaryRequest {
  readonly listingId: string;
  readonly cabin: CabinConfiguration;
}

export interface LeaseAircraftRequest {
  readonly typeId: string;
  readonly subType: LeaseSubType;
  readonly termMonths: number;
  /** Airframe age in years when the lease starts. */
  readonly ageYears: number;
  readonly cabin: CabinConfiguration;
}

export interface SaleLeasebackRequest {
  /** Owned airframe to sell. Must currently hold PURCHASE terms. */
  readonly aircraft: AircraftInstance;
  readonly termMonths: number;
}

export interface TerminateLeaseRequest {
  readonly registration: Registration;
  /** Current airframe record; used to check the aircraft is free and its return condition. */
  readonly aircraft: AircraftInstance;
}

/** Cash movements that the caller posts to the ledger, plus the new state. */
export interface FleetStep {
  readonly state: FleetAcquisitionState;
  readonly cashMovements: readonly FleetCashMovement[];
}

export type FactoryOrderStep = FleetStep & { readonly order: FactoryOrder };
export type SecondaryPurchaseStep = FleetStep & { readonly aircraft: AircraftInstance };
export type LeaseStep = FleetStep & { readonly aircraft: AircraftInstance; readonly lease: LeaseContract };
export type SaleLeasebackStep = FleetStep & {
  readonly aircraft: AircraftInstance;
  readonly lease: LeaseContract;
  readonly proceedsUSD: UsdAmount;
};

export type LeaseExitOutcome = 'RETURNED' | 'OWNERSHIP_TRANSFERRED';

export type TerminationStep = FleetStep & {
  readonly outcome: LeaseExitOutcome;
  /** The airframe after the exit: null if returned, updated terms if title transferred. */
  readonly aircraft: AircraftInstance | null;
};

/** Output of the factory delivery sweep. */
export interface DeliveryStep extends FleetStep {
  readonly deliveries: readonly AircraftInstance[];
}

/** Output of the daily lease cycle. */
export interface DailyLeaseStep extends FleetStep {
  /** Registrations returned to lessors this cycle. */
  readonly returnedRegistrations: readonly Registration[];
  /** Registrations whose finance term completed; the caller should set their terms to PURCHASE. */
  readonly transferredRegistrations: readonly Registration[];
}

export interface CreateFleetStateInput {
  readonly worldSeed: number;
  readonly nextSequence: number;
  readonly ctx: FleetContext;
}

/* ========================================================================== *
 * 2. SMALL HELPERS
 * ========================================================================== */

function reject(code: AcquisitionRejectionCode, message: string): AcquisitionRejection {
  return { ok: false, code, message };
}

function dayOf(minutes: SimMinutes): number {
  return Math.floor(minutes / MINUTES_PER_DAY);
}

function registrationFor(sequence: number): Registration {
  return `FL-${pad(sequence, 3)}`;
}

function upsertAirframe(airframes: readonly AirframeProfile[], profile: AirframeProfile): AirframeProfile[] {
  return [...airframes.filter((a) => a.registration !== profile.registration), profile];
}

function withoutAirframes(airframes: readonly AirframeProfile[], registrations: readonly Registration[]): AirframeProfile[] {
  return airframes.filter((a) => !registrations.includes(a.registration));
}

function findLeaseByRegistration(leases: readonly LeaseContract[], registration: Registration): LeaseContract | undefined {
  return leases.find((lease) => lease.registration === registration);
}

/** Interest and principal split of one level instalment on a finance lease. */
function splitInstalment(outstandingUSD: UsdAmount, instalmentUSD: UsdAmount, annualRatePct: number): {
  readonly interestUSD: UsdAmount;
  readonly principalUSD: UsdAmount;
} {
  const interestUSD = outstandingUSD * (annualRatePct / 100 / MONTHS_PER_YEAR);
  return { interestUSD, principalUSD: instalmentUSD - interestUSD };
}

function monthsBetween(fromMinutes: SimMinutes, toMinutes: SimMinutes): number {
  return (toMinutes - fromMinutes) / MINUTES_PER_MONTH;
}

/* ========================================================================== *
 * 3. AIRFRAME BUILDING
 * ========================================================================== */

interface AirframeSpec {
  readonly registration: Registration;
  readonly type: AircraftType;
  readonly cabin: CabinConfiguration;
  readonly baseAirportId: AirportId;
  readonly acquisition: AcquisitionTerms;
  readonly deliveredAt: SimMinutes;
  readonly manufactureYear: number;
  readonly flightHours: number;
  readonly cycles: number;
  readonly conditionPct: number;
  readonly yearsUntilDCheck: number;
  /** False for a new airframe with no D-Check behind it yet. */
  readonly hasDCheckHistory: boolean;
}

function buildAirframe(spec: AirframeSpec): AircraftInstance {
  const interval = spec.type.maintenance.dCheckIntervalYears;
  const nextDCheckDueAt = spec.deliveredAt + yearsToMinutes(spec.yearsUntilDCheck);
  const lastDCheckCompletedAt = spec.hasDCheckHistory ? nextDCheckDueAt - yearsToMinutes(interval) : null;
  const maintenance: MaintenanceStatus = {
    airworthiness: 'SERVICEABLE',
    flightHoursSinceACheck: spec.flightHours % spec.type.maintenance.aCheckIntervalFlightHours,
    flightHoursSinceCCheck: spec.flightHours % spec.type.maintenance.cCheckIntervalFlightHours,
    lastDCheckCompletedAt,
    nextDCheckDueAt,
    conditionPct: spec.conditionPct,
    activeCheck: null,
    history: [],
  };
  return {
    registration: spec.registration,
    typeId: spec.type.id,
    familyId: spec.type.familyId,
    acquisition: spec.acquisition,
    deliveredAt: spec.deliveredAt,
    manufactureYear: spec.manufactureYear,
    totalFlightHours: spec.flightHours,
    totalCycles: spec.cycles,
    cabin: spec.cabin,
    maintenance,
    location: { kind: 'AT_AIRPORT', airportId: spec.baseAirportId },
    state: 'IDLE',
    assignedScheduleId: null,
    baseAirportId: spec.baseAirportId,
  };
}

/** Maintenance picture for a leased airframe of a given age, synthesised from the type's cycle. */
function synthesisedHistory(type: AircraftType, ageYears: number): {
  readonly flightHours: number;
  readonly cycles: number;
  readonly conditionPct: number;
  readonly yearsUntilDCheck: number;
} {
  const interval = type.maintenance.dCheckIntervalYears;
  const flightHours = ageYears * SYNTHETIC_HOURS_PER_YEAR;
  const yearsSinceDCheck = Math.min(ageYears, interval * 0.5);
  return {
    flightHours,
    cycles: Math.round(flightHours / SYNTHETIC_HOURS_PER_CYCLE),
    conditionPct: Math.round(clamp(100 - 1.6 * ageYears, 40, 100)),
    yearsUntilDCheck: interval - yearsSinceDCheck,
  };
}

/* ========================================================================== *
 * 4. STATE CONSTRUCTION AND CATALOGUE
 * ========================================================================== */

export function createFleetAcquisitionState(input: CreateFleetStateInput): FleetAcquisitionState {
  return {
    worldSeed: input.worldSeed,
    market: input.ctx.market,
    marketMonthIndex: monthIndexOfDay(dayOf(input.ctx.now)),
    catalog: buildSecondaryCatalog({
      worldSeed: input.worldSeed,
      day: dayOf(input.ctx.now),
      types: input.ctx.types,
      market: input.ctx.market,
    }),
    factoryOrders: [],
    leases: [],
    airframes: [],
    nextSequence: input.nextSequence,
  };
}

/** Rolls the secondary catalogue over when the month changes. Safe to call every tick. */
export function refreshFleetCatalog(state: FleetAcquisitionState, ctx: FleetContext): FleetAcquisitionState {
  const catalog = refreshSecondaryCatalog(state.catalog, {
    worldSeed: state.worldSeed,
    day: dayOf(ctx.now),
    types: ctx.types,
    market: ctx.market,
  });
  return catalog === state.catalog ? state : { ...state, catalog };
}

/* ========================================================================== *
 * 5. FACTORY ORDERS
 * ========================================================================== */

function validateFactoryRequest(
  typeId: string,
  deliveryLeadMonths: number,
  ctx: FleetContext
): AircraftType | AcquisitionRejection {
  const type = ctx.types[typeId];
  if (type === undefined) {
    return reject('UNKNOWN_TYPE', `Unknown aircraft type ${typeId}.`);
  }
  if (!Number.isInteger(deliveryLeadMonths) || deliveryLeadMonths < FACTORY_LEAD_MONTHS_MIN || deliveryLeadMonths > FACTORY_LEAD_MONTHS_MAX) {
    return reject(
      'LEAD_TIME_OUT_OF_RANGE',
      `Factory lead time must be a whole number of ${FACTORY_LEAD_MONTHS_MIN}–${FACTORY_LEAD_MONTHS_MAX} months.`
    );
  }
  const deliveryAt = ctx.now + deliveryLeadMonths * MINUTES_PER_MONTH;
  if (type.productionEndYear !== null && calendarYearOf(deliveryAt) > type.productionEndYear) {
    return reject('PRODUCTION_ENDED', `${type.name} is out of production before that delivery date.`);
  }
  return type;
}

/** Quote for a factory order at the current market, without placing it. */
export function quoteFactoryOrder(
  request: { readonly typeId: string; readonly deliveryLeadMonths: number },
  ctx: FleetContext
): FleetResult<{ readonly quote: FactoryQuote }> {
  const checked = validateFactoryRequest(request.typeId, request.deliveryLeadMonths, ctx);
  if ('ok' in checked) return checked;
  const lockedPriceUSD = factoryLockedPriceUSD(checked, request.deliveryLeadMonths, ctx.market);
  const depositUSD = factoryDepositUSD(lockedPriceUSD);
  return {
    ok: true,
    quote: {
      typeId: checked.id,
      deliveryLeadMonths: request.deliveryLeadMonths,
      deliveryAt: ctx.now + request.deliveryLeadMonths * MINUTES_PER_MONTH,
      listPriceUSD: factoryListPriceUSD(checked, ctx.market),
      lockedPriceUSD,
      depositUSD,
      balanceAtDeliveryUSD: lockedPriceUSD - depositUSD,
    },
  };
}

export function placeFactoryOrder(
  state: FleetAcquisitionState,
  request: FactoryOrderRequest,
  ctx: FleetContext
): FleetResult<FactoryOrderStep> {
  const checked = validateFactoryRequest(request.typeId, request.deliveryLeadMonths, ctx);
  if ('ok' in checked) return checked;

  const lockedPriceUSD = factoryLockedPriceUSD(checked, request.deliveryLeadMonths, ctx.market);
  const depositUSD = factoryDepositUSD(lockedPriceUSD);
  if (depositUSD > ctx.cashUSD) {
    return reject('INSUFFICIENT_CASH', `Deposit of ${Math.round(depositUSD)} exceeds available cash.`);
  }

  const sequence = state.nextSequence;
  const registration = registrationFor(sequence);
  const order: FactoryOrder = {
    id: `FO-${pad(sequence, 3)}`,
    typeId: checked.id,
    baseAirportId: ctx.baseAirportId,
    cabin: request.cabin,
    registration,
    orderedAt: ctx.now,
    deliveryAt: ctx.now + request.deliveryLeadMonths * MINUTES_PER_MONTH,
    deliveryLeadMonths: request.deliveryLeadMonths,
    lockedPriceUSD,
    depositUSD,
    status: 'OPEN',
  };

  return {
    ok: true,
    order,
    state: { ...state, factoryOrders: [...state.factoryOrders, order], nextSequence: sequence + 1 },
    cashMovements: [
      {
        category: 'AIRCRAFT_PURCHASE',
        amountUSD: -depositUSD,
        description: `Factory deposit ${order.id} (${checked.name}, delivery in ${request.deliveryLeadMonths} months)`,
        registration,
      },
    ],
  };
}

/**
 * Cancels an open factory order. The penalty is a share of the locked price
 * that rises as delivery approaches. The deposit is credited against it: a
 * shortfall is charged, a surplus is refunded.
 */
export function cancelFactoryOrder(
  state: FleetAcquisitionState,
  orderId: string,
  ctx: FleetContext
): FleetResult<FleetStep> {
  const order = state.factoryOrders.find((o) => o.id === orderId);
  if (order === undefined) {
    return reject('ORDER_NOT_FOUND', `No factory order ${orderId}.`);
  }
  if (order.status !== 'OPEN' || order.deliveryAt <= ctx.now) {
    return reject('ORDER_NOT_OPEN', `Order ${orderId} can no longer be cancelled.`);
  }

  const penaltyUSD = order.lockedPriceUSD * factoryCancellationRate(monthsBetween(ctx.now, order.deliveryAt));
  const netUSD = penaltyUSD - order.depositUSD;
  const cashMovements: FleetCashMovement[] = [];
  if (netUSD !== 0) {
    cashMovements.push({
      category: 'AIRCRAFT_PURCHASE',
      amountUSD: -netUSD,
      description: netUSD > 0 ? `Cancellation penalty ${order.id}` : `Deposit refund ${order.id}`,
      registration: order.registration,
    });
  }

  return {
    ok: true,
    state: {
      ...state,
      factoryOrders: state.factoryOrders.map((o): FactoryOrder => (o.id === orderId ? { ...o, status: 'CANCELLED' } : o)),
    },
    cashMovements,
  };
}

/**
 * Delivers every open order whose delivery date has arrived. The balance is
 * charged at delivery whatever the cash position. A negative balance is the
 * caller's to resolve.
 */
export function deliverDueFactoryOrders(state: FleetAcquisitionState, ctx: FleetContext): DeliveryStep {
  const cashMovements: FleetCashMovement[] = [];
  const deliveries: AircraftInstance[] = [];
  let airframes: readonly AirframeProfile[] = state.airframes;
  const factoryOrders: FactoryOrder[] = [];

  for (const order of state.factoryOrders) {
    if (order.status !== 'OPEN' || order.deliveryAt > ctx.now) {
      factoryOrders.push(order);
      continue;
    }
    const type = ctx.types[order.typeId];
    if (type === undefined) {
      throw new Error(`Factory order ${order.id} references unknown type ${order.typeId}.`);
    }
    const balanceUSD = order.lockedPriceUSD - order.depositUSD;
    cashMovements.push({
      category: 'AIRCRAFT_PURCHASE',
      amountUSD: -balanceUSD,
      description: `Delivery balance ${order.id}`,
      registration: order.registration,
    });
    deliveries.push(
      buildAirframe({
        registration: order.registration,
        type,
        cabin: order.cabin,
        baseAirportId: order.baseAirportId,
        acquisition: {
          type: 'PURCHASE',
          acquiredAt: order.deliveryAt,
          purchasePriceUSD: order.lockedPriceUSD,
        } satisfies PurchaseTerms,
        deliveredAt: order.deliveryAt,
        manufactureYear: calendarYearOf(order.deliveryAt),
        flightHours: 0,
        cycles: 0,
        conditionPct: 100,
        yearsUntilDCheck: type.maintenance.dCheckIntervalYears,
        hasDCheckHistory: false,
      })
    );
    airframes = upsertAirframe(airframes, { registration: order.registration, fuelBurnMultiplier: 1 });
    factoryOrders.push({ ...order, status: 'DELIVERED' });
  }

  return {
    state: { ...state, factoryOrders, airframes: [...airframes] },
    cashMovements,
    deliveries,
  };
}

/* ========================================================================== *
 * 6. SECONDARY MARKET PURCHASE
 * ========================================================================== */

export function purchaseSecondaryAircraft(
  state: FleetAcquisitionState,
  request: PurchaseSecondaryRequest,
  ctx: FleetContext
): FleetResult<SecondaryPurchaseStep> {
  const refreshed = refreshFleetCatalog(state, ctx);
  const listing: SecondaryListing | undefined = refreshed.catalog.listings.find(
    (l) => l.listingId === request.listingId
  );
  if (listing === undefined) {
    return reject('LISTING_NOT_FOUND', `No secondary listing ${request.listingId} this month.`);
  }
  if (dayOf(ctx.now) >= listing.expiresOnDay) {
    return reject('LISTING_EXPIRED', `Listing ${request.listingId} has expired.`);
  }
  const type = ctx.types[listing.typeId];
  if (type === undefined) {
    return reject('UNKNOWN_TYPE', `Unknown aircraft type ${listing.typeId}.`);
  }
  if (listing.askingPriceUSD > ctx.cashUSD) {
    return reject('INSUFFICIENT_CASH', `Asking price of ${Math.round(listing.askingPriceUSD)} exceeds available cash.`);
  }

  const aircraft = buildAirframe({
    registration: listing.registration,
    type,
    cabin: request.cabin,
    baseAirportId: ctx.baseAirportId,
    acquisition: {
      type: 'PURCHASE',
      acquiredAt: ctx.now,
      purchasePriceUSD: listing.askingPriceUSD,
    } satisfies PurchaseTerms,
    deliveredAt: ctx.now,
    manufactureYear: listing.manufactureYear,
    flightHours: listing.airframeHours,
    cycles: listing.cycles,
    conditionPct: listing.conditionPct,
    yearsUntilDCheck: listing.yearsUntilDCheck,
    hasDCheckHistory: true,
  });

  return {
    ok: true,
    aircraft,
    state: {
      ...refreshed,
      catalog: {
        ...refreshed.catalog,
        listings: refreshed.catalog.listings.filter((l) => l.listingId !== listing.listingId),
      },
      airframes: upsertAirframe(refreshed.airframes, {
        registration: listing.registration,
        fuelBurnMultiplier: listing.fuelBurnMultiplier,
      }),
    },
    cashMovements: [
      {
        category: 'AIRCRAFT_PURCHASE',
        amountUSD: -listing.askingPriceUSD,
        description: `Purchase of used ${type.name} (${listing.ageYears} years, ${listing.airframeHours} hours)`,
        registration: listing.registration,
      },
    ],
  };
}

/* ========================================================================== *
 * 7. LEASES
 * ========================================================================== */

function leaseTermRange(subType: LeaseSubType): { readonly min: number; readonly max: number } {
  switch (subType) {
    case 'DRY':
      return DRY_LEASE_TERM_MONTHS;
    case 'WET':
      return WET_LEASE_TERM_MONTHS;
    case 'FINANCE':
      return FINANCE_LEASE_TERM_MONTHS;
    default: {
      const exhaustive: never = subType;
      throw new Error(`Unhandled lease sub-type ${String(exhaustive)}`);
    }
  }
}

function checkTerm(subType: LeaseSubType, termMonths: number): AcquisitionRejection | null {
  const range = leaseTermRange(subType);
  if (!Number.isInteger(termMonths) || termMonths < range.min || termMonths > range.max) {
    return reject('TERM_OUT_OF_RANGE', `${subType} lease term must be ${range.min}–${range.max} months.`);
  }
  return null;
}

function checkAge(ageYears: number): AcquisitionRejection | null {
  if (!Number.isInteger(ageYears) || ageYears < LEASE_AGE_YEARS.min || ageYears > LEASE_AGE_YEARS.max) {
    return reject('AGE_OUT_OF_RANGE', `Lease airframe age must be ${LEASE_AGE_YEARS.min}–${LEASE_AGE_YEARS.max} years.`);
  }
  return null;
}

/** Price of a lease at the current market, as LEASE_AIRCRAFT would charge it. */
export interface LeaseQuote {
  readonly monthlyPaymentUSD: UsdAmount;
  /** Refundable deposit (dry), finance down payment, or zero (wet). */
  readonly depositUSD: UsdAmount;
  /** Deposit plus the first instalment, charged at signing. */
  readonly upfrontUSD: UsdAmount;
  /** Finance only; zero for dry and wet. */
  readonly assetPriceUSD: UsdAmount;
  readonly financedUSD: UsdAmount;
  readonly balloonUSD: UsdAmount;
  readonly annualRatePct: number;
}

export function quoteLease(
  subType: LeaseSubType,
  termMonths: number,
  ageYears: number,
  type: AircraftType,
  market: MarketEnvironment
): LeaseQuote {
  if (subType === 'FINANCE') {
    const assetPriceUSD = appraisedValueUSD(type, ageYears, market);
    const downPaymentUSD = assetPriceUSD * FINANCE_DOWN_PAYMENT_RATE;
    const financedUSD = assetPriceUSD - downPaymentUSD;
    const balloonUSD = assetPriceUSD * FINANCE_BALLOON_RATE;
    const annualRatePct = financeAnnualRatePct(market);
    const monthlyPaymentUSD = monthlyInstalmentUSD(financedUSD, balloonUSD, annualRatePct, termMonths);
    return {
      monthlyPaymentUSD,
      depositUSD: downPaymentUSD,
      upfrontUSD: downPaymentUSD + monthlyPaymentUSD,
      assetPriceUSD,
      financedUSD,
      balloonUSD,
      annualRatePct,
    };
  }
  const monthlyPaymentUSD =
    subType === 'WET' ? wetMonthlyFeeUSD(type, ageYears, market) : dryMonthlyRentUSD(type, ageYears, market);
  const depositUSD = subType === 'DRY' ? monthlyPaymentUSD * DRY_LEASE_DEPOSIT_MONTHS : 0;
  return {
    monthlyPaymentUSD,
    depositUSD,
    upfrontUSD: depositUSD + monthlyPaymentUSD,
    assetPriceUSD: 0,
    financedUSD: 0,
    balloonUSD: 0,
    annualRatePct: 0,
  };
}

/**
 * Opens a lease. Upfront cash is the deposit (or finance down payment) plus the
 * first monthly instalment, which is charged at signing.
 */
export function leaseAircraft(
  state: FleetAcquisitionState,
  request: LeaseAircraftRequest,
  ctx: FleetContext
): FleetResult<LeaseStep> {
  const type = ctx.types[request.typeId];
  if (type === undefined) {
    return reject('UNKNOWN_TYPE', `Unknown aircraft type ${request.typeId}.`);
  }
  const ageError = checkAge(request.ageYears);
  if (ageError !== null) return ageError;
  const termError = checkTerm(request.subType, request.termMonths);
  if (termError !== null) return termError;

  const sequence = state.nextSequence;
  const registration = registrationFor(sequence);
  const startedAt = ctx.now;
  const endsAt = startedAt + request.termMonths * MINUTES_PER_MONTH;
  const history = synthesisedHistory(type, request.ageYears);
  const interval = type.maintenance.dCheckIntervalYears;
  const acquisitionBase = {
    acquiredAt: startedAt,
    termMonths: request.termMonths,
    leaseEndsAt: endsAt,
  };
  const leaseBase = (
    monthlyPaymentUSD: UsdAmount,
    depositUSD: UsdAmount,
    dCheckRequiredOnReturn: boolean
  ): LeaseContractBase => ({
    id: `LSE-${pad(sequence, 3)}`,
    registration,
    typeId: type.id,
    baseAirportId: ctx.baseAirportId,
    origin: 'NEW_LEASE',
    startedAt,
    endsAt,
    termMonths: request.termMonths,
    ageYearsAtStart: request.ageYears,
    monthlyPaymentUSD,
    depositUSD,
    paymentsMade: 1,
    status: 'ACTIVE',
    dCheckRequiredOnReturn,
  });

  const airframeBase = {
    registration,
    type,
    cabin: request.cabin,
    baseAirportId: ctx.baseAirportId,
    deliveredAt: startedAt,
    manufactureYear: calendarYearOf(startedAt) - request.ageYears,
    flightHours: history.flightHours,
    cycles: history.cycles,
    conditionPct: history.conditionPct,
    yearsUntilDCheck: history.yearsUntilDCheck,
    hasDCheckHistory: request.ageYears > 0 && interval > 0,
  };

  if (request.subType === 'FINANCE') {
    const quote = quoteLease('FINANCE', request.termMonths, request.ageYears, type, ctx.market);
    const {
      assetPriceUSD,
      financedUSD,
      balloonUSD,
      annualRatePct,
      monthlyPaymentUSD: instalmentUSD,
      depositUSD: downPaymentUSD,
      upfrontUSD,
    } = quote;
    const firstSplit = splitInstalment(financedUSD, instalmentUSD, annualRatePct);
    if (upfrontUSD > ctx.cashUSD) {
      return reject('INSUFFICIENT_CASH', `Down payment and first instalment total ${Math.round(upfrontUSD)}.`);
    }
    const outstandingPrincipalUSD = financedUSD - firstSplit.principalUSD;
    const lease: LeaseContract = {
      ...leaseBase(instalmentUSD, downPaymentUSD, false),
      subType: 'FINANCE',
      annualRatePct,
      assetPriceUSD,
      outstandingPrincipalUSD,
      balloonUSD,
    };
    const terms: FinanceLeaseTerms = {
      type: 'FINANCE_LEASE',
      ...acquisitionBase,
      monthlyPaymentUSD: instalmentUSD,
      residualValueUSD: balloonUSD,
      outstandingPrincipalUSD,
      ownershipTransfersAtEnd: true,
    };
    const aircraft = buildAirframe({ ...airframeBase, acquisition: terms });
    return {
      ok: true,
      aircraft,
      lease,
      state: financeState(state, lease, sequence),
      cashMovements: [
        { category: 'AIRCRAFT_PURCHASE', amountUSD: -downPaymentUSD, description: `Finance down payment ${registration}`, registration },
        { category: 'FINANCING_INTEREST', amountUSD: -firstSplit.interestUSD, description: `Finance interest 1/${request.termMonths} ${registration}`, registration },
        { category: 'LOAN_REPAYMENT', amountUSD: -firstSplit.principalUSD, description: `Finance principal 1/${request.termMonths} ${registration}`, registration },
      ],
    };
  }

  const {
    monthlyPaymentUSD: monthlyUSD,
    depositUSD,
    upfrontUSD,
  } = quoteLease(request.subType, request.termMonths, request.ageYears, type, ctx.market);
  if (upfrontUSD > ctx.cashUSD) {
    return reject('INSUFFICIENT_CASH', `Deposit and first instalment total ${Math.round(upfrontUSD)}.`);
  }

  const dCheckRequiredOnReturn = request.subType === 'DRY';
  const lease: LeaseContract =
    request.subType === 'WET'
      ? { ...leaseBase(monthlyUSD, depositUSD, dCheckRequiredOnReturn), subType: 'WET' }
      : { ...leaseBase(monthlyUSD, depositUSD, dCheckRequiredOnReturn), subType: 'DRY' };
  const terms: OperatingLeaseTerms = {
    type: 'OPERATING_LEASE',
    ...acquisitionBase,
    monthlyPaymentUSD: monthlyUSD,
    dCheckRequiredOnReturn,
  };
  const aircraft = buildAirframe({ ...airframeBase, acquisition: terms });

  const cashMovements: FleetCashMovement[] = [];
  if (depositUSD > 0) {
    cashMovements.push({ category: 'AIRCRAFT_PURCHASE', amountUSD: -depositUSD, description: `Lease deposit ${registration}`, registration });
  }
  cashMovements.push({ category: 'LEASE_PAYMENT', amountUSD: -monthlyUSD, description: `Lease instalment 1/${request.termMonths} ${registration}`, registration });

  return {
    ok: true,
    aircraft,
    lease,
    state: {
      ...state,
      leases: [...state.leases, lease],
      airframes: upsertAirframe(state.airframes, {
        registration,
        fuelBurnMultiplier: fuelBurnMultiplierForAge(request.ageYears),
      }),
      nextSequence: sequence + 1,
    },
    cashMovements,
  };
}

function financeState(state: FleetAcquisitionState, lease: LeaseContract, sequence: number): FleetAcquisitionState {
  return {
    ...state,
    leases: [...state.leases, lease],
    airframes: upsertAirframe(state.airframes, {
      registration: lease.registration,
      fuelBurnMultiplier: fuelBurnMultiplierForAge(lease.ageYearsAtStart),
    }),
    nextSequence: sequence + 1,
  };
}

/* ========================================================================== *
 * 8. SALE AND LEASEBACK
 * ========================================================================== */

/**
 * Sells an owned airframe to a lessor and leases it back as a dry lease. The
 * sale price is a set share of appraised value. The airline pays the lease
 * deposit and first instalment from the proceeds and any remaining cash.
 */
export function sellAndLeaseBack(
  state: FleetAcquisitionState,
  request: SaleLeasebackRequest,
  ctx: FleetContext
): FleetResult<SaleLeasebackStep> {
  const { aircraft } = request;
  if (aircraft.acquisition.type !== 'PURCHASE') {
    return reject('AIRCRAFT_NOT_OWNED', `${aircraft.registration} is not owned outright.`);
  }
  if (aircraft.state === 'IN_FLIGHT' || aircraft.maintenance.activeCheck !== null) {
    return reject('AIRCRAFT_NOT_AVAILABLE', `${aircraft.registration} is in flight or in a check.`);
  }
  const termError = checkTerm('DRY', request.termMonths);
  if (termError !== null) return termError;
  const type = ctx.types[aircraft.typeId];
  if (type === undefined) {
    return reject('UNKNOWN_TYPE', `Unknown aircraft type ${aircraft.typeId}.`);
  }

  const ageYears = airframeAgeYears(aircraft.manufactureYear, ctx.now);
  const proceedsUSD = appraisedValueUSD(type, ageYears, ctx.market) * SALE_LEASEBACK_VALUE_FACTOR;
  if (!(proceedsUSD > 0)) {
    return reject('SALE_VALUE_INVALID', `No positive sale value for ${aircraft.registration}.`);
  }
  const monthlyUSD = dryMonthlyRentUSD(type, ageYears, ctx.market);
  const depositUSD = monthlyUSD * DRY_LEASE_DEPOSIT_MONTHS;
  const upfrontUSD = depositUSD + monthlyUSD;
  if (ctx.cashUSD + proceedsUSD < upfrontUSD) {
    return reject('INSUFFICIENT_CASH', `Lease deposit and first instalment total ${Math.round(upfrontUSD)}.`);
  }

  const endsAt = ctx.now + request.termMonths * MINUTES_PER_MONTH;
  const lease: LeaseContract = {
    id: `SLB-${pad(state.nextSequence, 3)}`,
    registration: aircraft.registration,
    typeId: aircraft.typeId,
    baseAirportId: aircraft.baseAirportId,
    origin: 'SALE_LEASEBACK',
    startedAt: ctx.now,
    endsAt,
    termMonths: request.termMonths,
    ageYearsAtStart: ageYears,
    monthlyPaymentUSD: monthlyUSD,
    depositUSD,
    paymentsMade: 1,
    status: 'ACTIVE',
    dCheckRequiredOnReturn: true,
    subType: 'DRY',
  };
  const updated: AircraftInstance = {
    ...aircraft,
    acquisition: {
      type: 'OPERATING_LEASE',
      acquiredAt: ctx.now,
      monthlyPaymentUSD: monthlyUSD,
      termMonths: request.termMonths,
      leaseEndsAt: endsAt,
      dCheckRequiredOnReturn: true,
    } satisfies OperatingLeaseTerms,
  };

  return {
    ok: true,
    aircraft: updated,
    lease,
    proceedsUSD,
    state: {
      ...state,
      leases: [...state.leases, lease],
      airframes: upsertAirframe(state.airframes, {
        registration: aircraft.registration,
        fuelBurnMultiplier: fuelBurnMultiplierForAge(ageYears),
      }),
      nextSequence: state.nextSequence + 1,
    },
    cashMovements: [
      { category: 'AIRCRAFT_SALE', amountUSD: proceedsUSD, description: `Sale to lessor ${aircraft.registration}`, registration: aircraft.registration },
      { category: 'AIRCRAFT_PURCHASE', amountUSD: -depositUSD, description: `Leaseback deposit ${aircraft.registration}`, registration: aircraft.registration },
      { category: 'LEASE_PAYMENT', amountUSD: -monthlyUSD, description: `Leaseback instalment 1/${request.termMonths} ${aircraft.registration}`, registration: aircraft.registration },
    ],
  };
}

/* ========================================================================== *
 * 9. TERMINATION AND RETURN CONDITION
 * ========================================================================== */

/**
 * Lessor charge if the airframe comes back with less than six months of D-Check
 * validity. Charged at the type's D-Check cost; zero when the lease does not
 * require a D-Check on return or the condition is met.
 */
function returnShortfallUSD(
  lease: LeaseContract,
  aircraft: AircraftInstance | undefined,
  type: AircraftType | undefined,
  now: SimMinutes
): UsdAmount {
  if (!lease.dCheckRequiredOnReturn || aircraft === undefined || type === undefined) return 0;
  const validityMonths = monthsBetween(now, aircraft.maintenance.nextDCheckDueAt);
  return validityMonths >= RETURN_MIN_D_CHECK_MONTHS ? 0 : type.maintenance.dCheckCostUSD;
}

/**
 * Ends a lease early. Dry leases pay half the remaining rent and forfeit the
 * deposit. Wet leases pay one month's notice fee. Finance leases pay off the
 * outstanding principal plus a prepayment fee, and title transfers.
 */
export function terminateLease(
  state: FleetAcquisitionState,
  request: TerminateLeaseRequest,
  ctx: FleetContext
): FleetResult<TerminationStep> {
  const lease = findLeaseByRegistration(state.leases, request.registration);
  if (lease === undefined) {
    return reject('LEASE_NOT_FOUND', `No lease on ${request.registration}.`);
  }
  if (lease.status !== 'ACTIVE') {
    return reject('LEASE_NOT_ACTIVE', `Lease on ${request.registration} is ${lease.status.toLowerCase()}.`);
  }
  const { aircraft } = request;
  if (aircraft.state === 'IN_FLIGHT' || aircraft.maintenance.activeCheck !== null) {
    return reject('AIRCRAFT_NOT_AVAILABLE', `${aircraft.registration} is in flight or in a check.`);
  }

  const remainingInstalments = lease.termMonths - lease.paymentsMade;
  const registration = lease.registration;

  if (lease.subType === 'FINANCE') {
    const payoffUSD = lease.outstandingPrincipalUSD;
    const feeUSD = payoffUSD * FINANCE_PREPAYMENT_FEE_RATE;
    const transferred: AircraftInstance = {
      ...aircraft,
      acquisition: {
        type: 'PURCHASE',
        acquiredAt: ctx.now,
        purchasePriceUSD: lease.assetPriceUSD,
      } satisfies PurchaseTerms,
    };
    return {
      ok: true,
      outcome: 'OWNERSHIP_TRANSFERRED',
      aircraft: transferred,
      state: {
        ...state,
        leases: state.leases.map((l): LeaseContract => (l.id === lease.id ? { ...lease, status: 'OWNED', outstandingPrincipalUSD: 0 } : l)),
      },
      cashMovements: [
        { category: 'LOAN_REPAYMENT', amountUSD: -payoffUSD, description: `Finance payoff ${registration}`, registration },
        { category: 'FINANCING_INTEREST', amountUSD: -feeUSD, description: `Prepayment fee ${registration}`, registration },
      ],
    };
  }

  const feeUSD =
    lease.subType === 'WET'
      ? lease.monthlyPaymentUSD * WET_EARLY_TERMINATION_MONTHS
      : lease.monthlyPaymentUSD * remainingInstalments * DRY_EARLY_TERMINATION_RENT_SHARE;
  const shortfallUSD = returnShortfallUSD(lease, aircraft, ctx.types[lease.typeId], ctx.now);
  const cashMovements: FleetCashMovement[] = [
    { category: 'LEASE_PAYMENT', amountUSD: -feeUSD, description: `Early termination ${registration}`, registration },
  ];
  if (shortfallUSD > 0) {
    cashMovements.push({ category: 'MAINTENANCE', amountUSD: -shortfallUSD, description: `D-Check on return ${registration}`, registration });
  }

  return {
    ok: true,
    outcome: 'RETURNED',
    aircraft: null,
    state: {
      ...state,
      leases: state.leases.map((l): LeaseContract => (l.id === lease.id ? { ...lease, status: 'TERMINATED' } : l)),
      airframes: withoutAirframes(state.airframes, [registration]),
    },
    cashMovements,
  };
}

/* ========================================================================== *
 * 9b. OPENING LEASES
 * ========================================================================== */

export interface OpeningLeaseRequest {
  readonly registration: Registration;
  readonly typeId: string;
  readonly subType: 'DRY' | 'FINANCE';
  readonly termMonths: number;
  readonly ageYears: number;
  readonly startedAt: SimMinutes;
  readonly baseAirportId: AirportId;
}

/**
 * Records a lease already in force when the run starts (the starter fleet, or
 * a lease opened before the schedule existed). Nothing is charged at signing:
 * the first instalment falls due on the next daily cycle, so the lease runs
 * on the same schedule as any other. Finance opens with the financed balance
 * outstanding, the down payment being part of the opening balance sheet.
 */
export function registerOpeningLease(
  state: FleetAcquisitionState,
  request: OpeningLeaseRequest,
  ctx: FleetContext
): FleetResult<{ readonly state: FleetAcquisitionState; readonly lease: LeaseContract; readonly terms: AcquisitionTerms }> {
  const type = ctx.types[request.typeId];
  if (type === undefined) {
    return reject('UNKNOWN_TYPE', `Unknown aircraft type ${request.typeId}.`);
  }
  const termError = checkTerm(request.subType, request.termMonths);
  if (termError !== null) return termError;

  const quote = quoteLease(request.subType, request.termMonths, request.ageYears, type, ctx.market);
  const endsAt = request.startedAt + request.termMonths * MINUTES_PER_MONTH;
  const base: LeaseContractBase = {
    id: `LSE-${request.registration}`,
    registration: request.registration,
    typeId: type.id,
    baseAirportId: request.baseAirportId,
    origin: 'NEW_LEASE',
    startedAt: request.startedAt,
    endsAt,
    termMonths: request.termMonths,
    ageYearsAtStart: request.ageYears,
    monthlyPaymentUSD: quote.monthlyPaymentUSD,
    depositUSD: 0,
    paymentsMade: 0,
    status: 'ACTIVE',
    dCheckRequiredOnReturn: request.subType === 'DRY',
  };

  let lease: LeaseContract;
  let terms: AcquisitionTerms;
  if (request.subType === 'FINANCE') {
    const outstandingPrincipalUSD = quote.financedUSD;
    lease = {
      ...base,
      subType: 'FINANCE',
      annualRatePct: quote.annualRatePct,
      assetPriceUSD: quote.assetPriceUSD,
      outstandingPrincipalUSD,
      balloonUSD: quote.balloonUSD,
    };
    terms = {
      type: 'FINANCE_LEASE',
      acquiredAt: request.startedAt,
      monthlyPaymentUSD: quote.monthlyPaymentUSD,
      termMonths: request.termMonths,
      leaseEndsAt: endsAt,
      residualValueUSD: quote.balloonUSD,
      outstandingPrincipalUSD,
      ownershipTransfersAtEnd: true,
    };
  } else {
    lease = { ...base, subType: 'DRY' };
    terms = {
      type: 'OPERATING_LEASE',
      acquiredAt: request.startedAt,
      monthlyPaymentUSD: quote.monthlyPaymentUSD,
      termMonths: request.termMonths,
      leaseEndsAt: endsAt,
      dCheckRequiredOnReturn: true,
    };
  }

  return {
    ok: true,
    lease,
    terms,
    state: {
      ...state,
      leases: [...state.leases, lease],
      airframes: upsertAirframe(state.airframes, {
        registration: request.registration,
        fuelBurnMultiplier: fuelBurnMultiplierForAge(request.ageYears),
      }),
    },
  };
}

/* ========================================================================== *
 * 10. DAILY LEASE CYCLE
 * ========================================================================== */

/**
 * Posts every instalment that has fallen due and settles every term that has
 * ended. Run once per day with the current fleet. Dry and wet leases end by
 * return, with the deposit refunded less any return shortfall. Finance leases
 * pay the balloon and transfer title.
 */
export function dailyLeaseCycle(
  state: FleetAcquisitionState,
  ctx: FleetContext,
  fleet: readonly AircraftInstance[]
): DailyLeaseStep {
  const cashMovements: FleetCashMovement[] = [];
  const returnedRegistrations: Registration[] = [];
  const transferredRegistrations: Registration[] = [];

  const leases = state.leases.map((lease): LeaseContract => {
    if (lease.status !== 'ACTIVE') return lease;
    let current = lease;

    while (current.paymentsMade < current.termMonths && current.startedAt + current.paymentsMade * MINUTES_PER_MONTH <= ctx.now) {
      const instalment = current.monthlyPaymentUSD;
      const label = `${current.paymentsMade + 1}/${current.termMonths} ${current.registration}`;
      if (current.subType === 'FINANCE') {
        const split = splitInstalment(current.outstandingPrincipalUSD, instalment, current.annualRatePct);
        cashMovements.push(
          { category: 'FINANCING_INTEREST', amountUSD: -split.interestUSD, description: `Finance interest ${label}`, registration: current.registration },
          { category: 'LOAN_REPAYMENT', amountUSD: -split.principalUSD, description: `Finance principal ${label}`, registration: current.registration }
        );
        current = { ...current, paymentsMade: current.paymentsMade + 1, outstandingPrincipalUSD: current.outstandingPrincipalUSD - split.principalUSD };
      } else {
        cashMovements.push({ category: 'LEASE_PAYMENT', amountUSD: -instalment, description: `Lease instalment ${label}`, registration: current.registration });
        current = { ...current, paymentsMade: current.paymentsMade + 1 };
      }
    }

    if (ctx.now < current.endsAt) return current;

    if (current.subType === 'FINANCE') {
      cashMovements.push({
        category: 'LOAN_REPAYMENT',
        amountUSD: -current.outstandingPrincipalUSD,
        description: `Balloon and title transfer ${current.registration}`,
        registration: current.registration,
      });
      transferredRegistrations.push(current.registration);
      return { ...current, status: 'OWNED', outstandingPrincipalUSD: 0 };
    }

    const aircraft = fleet.find((a) => a.registration === current.registration);
    const type = ctx.types[current.typeId];
    const shortfallUSD = returnShortfallUSD(current, aircraft, type, ctx.now);
    // The deposit is capital, so its refund is too; a return shortfall is a maintenance cost.
    if (current.depositUSD > 0) {
      cashMovements.push({
        category: 'AIRCRAFT_PURCHASE',
        amountUSD: current.depositUSD,
        description: `Deposit refund ${current.registration}`,
        registration: current.registration,
      });
    }
    if (shortfallUSD > 0) {
      cashMovements.push({
        category: 'MAINTENANCE',
        amountUSD: -shortfallUSD,
        description: `Return shortfall ${current.registration}`,
        registration: current.registration,
      });
    }
    returnedRegistrations.push(current.registration);
    return { ...current, status: 'ENDED' };
  });

  return {
    state: {
      ...state,
      leases,
      airframes: withoutAirframes(state.airframes, returnedRegistrations),
    },
    cashMovements,
    returnedRegistrations,
    transferredRegistrations,
  };
}

/* ========================================================================== *
 * 11. QUERIES
 * ========================================================================== */

/** Fuel-burn multiplier for an airframe, 1.0 when no profile exists. */
export function fuelBurnMultiplierOf(state: FleetAcquisitionState, registration: Registration): number {
  return state.airframes.find((a) => a.registration === registration)?.fuelBurnMultiplier ?? 1;
}

/** Listings for the current month that have not expired on `now`. */
export function activeListings(state: FleetAcquisitionState, now: SimMinutes): readonly SecondaryListing[] {
  const day = dayOf(now);
  return state.catalog.listings.filter((listing) => listing.expiresOnDay > day);
}
